import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { useInstances } from '../context/InstanceContext';
import { useLiveQuizSession, useLiveQuizTimer, useLiveQuizNavigation, useQuizExport, useKeyboardShortcuts } from '../hooks';
import { RealtimeDBService } from '../firebase/unifiedDataService';
import { PageHeader, LoadingScreen, ErrorScreen, ConfirmDialog } from '../components/ui';
import {
  QuizSettingsModal,
  QRCodeFloatingPanel,
  LeaderboardModal,
  StatisticsModal,
  QuestionControlPanel,
  SessionActionsBar,
  LobbyScreen,
  TimerControls,
  QuestionCard
} from '../components/live-mode';
import BugReportModal from '../components/bug-report/BugReportModal';
import { Logger } from '../utils/logger';

const log = Logger.create('LiveMode');

/**
 * Live Quiz Admin View - Refactored Version
 * 
 * Fullscreen presenter interface für Live-Quizzes
 * 
 * REFACTORING NOTE:
 * Diese View nutzt jetzt Custom Hooks für:
 * - useLiveQuizSession: RTDB subscriptions & state
 * - useLiveQuizTimer: Timer logic
 * - useLiveQuizNavigation: Question navigation
 * 
 * Alle DB-Operationen laufen über unifiedDataService.
 * 
 * Features:
 * - Fragenoptionen (A, B, C, D) werden angezeigt
 * - Antworten sperren/freigeben mit Timer-Stop
 * - Richtige Antwort anzeigen mit grüner Markierung
 * - Antwortstatistiken mit Balkendiagramm
 * - QR-Code & Session-Link
 * - Timer-Anzeige & Steuerung
 * - Settings-Modal
 * - Deutsche Übersetzung
 * - Teilnehmer-Details Panel
 * - Export-Funktionen
 * - Keyboard Shortcuts
 */
function LiveModeView() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const { instances } = useInstances();

  // ============================================
  // CUSTOM HOOKS - Ersetzt die alten State/Effects
  // ============================================
  
  // Session Hook - alle RTDB subscriptions und Quiz state
  const session = useLiveQuizSession(instanceId);
  
  // Timer Hook - Timer-Logik mit Server-Sync
  const timer = useLiveQuizTimer({
    settings: session.settings,
    serverTimeOffset: session.serverTimeOffset,
    questionIndex: session.currentQuestionIndex,
    callbacks: {
      onTimerEnd: () => session.toggleAnswersLocked(true),
      onTimerSync: (timerState) => session.updateQuizState({ timerState })
    }
  });
  
  // Navigation Hook - Fragen-Navigation
  const nav = useLiveQuizNavigation({
    currentIndex: session.currentQuestionIndex,
    totalQuestions: session.questions.length,
    onNavigate: async (index) => {
      await session.goToQuestion(index);
      if (index >= 0) {
        timer.startTimer();
      } else {
        timer.stopTimer();
      }
    },
    onBeforeNext: async () => {
      // Auto-award points if enabled in settings
      const { settings: currentSettings, currentQuestionIndex, instanceId: currentInstanceId } = session;
      
      if (currentSettings?.autoAwardPointsOnNext && currentQuestionIndex >= 0 && user && currentInstanceId) {
        // Mark as points awarded
        const pointsAwardedPath = `quiz_instances/${user.uid}/${currentInstanceId}/points_awarded/${currentQuestionIndex}`;
        const alreadyAwarded = await RealtimeDBService.get(pointsAwardedPath);
        
        // Only if points NOT yet awarded (prevents animation on repeated clicks)
        if (!alreadyAwarded?.awarded) {
          log.info(`Auto-awarding points for question ${currentQuestionIndex} before moving to next`);
          
          // 1. First set points_awarded - student hooks will react to this via realtime listener
          await RealtimeDBService.set(pointsAwardedPath, {
            awarded: true,
            timestamp: Date.now()
          });
          
          // 2. Show correct answer briefly for visual feedback (independent of points calculation)
          await session.updateQuizState({ showCorrectAnswer: true });
          
          // 3. Animation duration only - points calculation happens independently via listener
          await new Promise(resolve => setTimeout(resolve, 1200));
          
          // 4. Hide correct answer
          await session.updateQuizState({ showCorrectAnswer: false });
          
          // 5. Small buffer before navigation
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
  });

  // ============================================
  // DERIVED VALUES (aus Hooks)
  // ============================================
  
  const {
    instance,
    instanceId: resolvedInstanceId,
    questions,
    settings,
    participants,
    allAnswers,
    currentAnswers,
    currentQuestionIndex,
    quizStarted,
    showResults,
    showCorrectAnswer,
    answersLocked,
    loading,
    error,
    serverTimeOffset
  } = session;

  const {
    timeRemaining,
    timerActive,
    timerPaused,
    timerEnabled
  } = timer;

  // Export Hook - für Fragen- und Ergebnis-Export
  const { exportQuestions, exportResults, canExportResults } = useQuizExport({
    questions,
    allAnswers,
    participants,
    sessionCode: instance?.sessionCode,
    instanceId: resolvedInstanceId
  });

  // ============================================
  // LOCAL UI STATE (nur UI-bezogen, nicht DB)
  // ============================================
  
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pendingSettings, setPendingSettings] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [showEndQuizConfirm, setShowEndQuizConfirm] = useState(false);
  const [showResetQuizConfirm, setShowResetQuizConfirm] = useState(false);

  // ============================================
  // WRAPPER HANDLERS - nutzen Hook-Methoden
  // ============================================

  // Navigation handlers (nutzen nav hook und session)
  const handleBackToLobby = useCallback(async () => {
    timer.stopTimer();
    await nav.goToLobby();
  }, [nav, timer]);

  const handleFirstQuestion = useCallback(async () => {
    await nav.goFirst();
  }, [nav]);

  const handlePreviousQuestion = useCallback(async () => {
    await nav.goPrevious();
  }, [nav]);

  const handleNextQuestion = useCallback(async () => {
    await nav.goNext();
  }, [nav]);

  const handleLastQuestion = useCallback(async () => {
    await nav.goLast();
  }, [nav]);

  // Quiz control handlers (nutzen session hook)
  const handleShowResults = useCallback(async () => {
    await session.toggleShowResults();
  }, [session]);

  const handleRevealAnswer = useCallback(async () => {
    // Beim Aufdecken auch Timer stoppen und Antworten sperren
    if (!showCorrectAnswer) {
      timer.stopTimer();
      await session.toggleAnswersLocked(true);
    }
    await session.toggleShowCorrectAnswer();
  }, [session, timer, showCorrectAnswer]);

  const handleLockAnswers = useCallback(async (forceLock = null) => {
    const newLocked = forceLock !== null ? forceLock : !answersLocked;
    if (newLocked) {
      timer.stopTimer();
    }
    await session.toggleAnswersLocked(newLocked);
  }, [session, timer, answersLocked]);

  // Timer handlers (nutzen timer hook)
  const handleStartTimer = useCallback(() => {
    timer.startTimer();
    session.toggleAnswersLocked(false);
  }, [timer, session]);

  const handleStopTimer = useCallback(() => {
    timer.stopTimer();
  }, [timer]);

  const handlePauseTimer = useCallback(() => {
    timer.pauseTimer();
  }, [timer]);

  const handleResumeTimer = useCallback(() => {
    timer.resumeTimer();
    if (answersLocked) {
      session.toggleAnswersLocked(false);
    }
  }, [timer, session, answersLocked]);

  const handleAddTime = useCallback(() => {
    timer.addBonusTime(20);
    if (answersLocked) {
      session.toggleAnswersLocked(false);
    }
  }, [timer, session, answersLocked]);

  // Quiz lifecycle handlers
  const handleStartQuiz = useCallback(async () => {
    await session.startQuiz();
    timer.startTimer();
  }, [session, timer]);

  const handleEndQuiz = useCallback(() => {
    setShowEndQuizConfirm(true);
  }, []);

  const confirmEndQuiz = useCallback(async () => {
    try {
      await session.endQuiz();
      setShowEndQuizConfirm(false);
      navigate(`/results/${resolvedInstanceId}`);
    } catch (err) {
      log.error('Fehler beim Beenden:', err);
    }
  }, [session, resolvedInstanceId, navigate]);

  const handleResetQuiz = useCallback(() => {
    setShowResetQuizConfirm(true);
  }, []);

  const confirmResetQuiz = useCallback(async () => {
    try {
      timer.stopTimer();
      await session.resetQuiz();
      setShowResetQuizConfirm(false);
    } catch (err) {
      log.error('Fehler beim Zurücksetzen:', err);
    }
  }, [session, timer]);

  // Link kopieren - mit kurzem Session Code
  const copyParticipantLink = useCallback(() => {
    const baseUrl = window.location.origin;
    const sessionCode = instance?.sessionCode || resolvedInstanceId;
    const participantUrl = `${baseUrl}/play/${sessionCode}`;
    
    navigator.clipboard.writeText(participantUrl)
      .then(() => {
        setCopySuccess('Link kopiert!');
        setTimeout(() => setCopySuccess(''), 2000);
      })
      .catch(() => {
        setCopySuccess('Fehler beim Kopieren');
      });
  }, [resolvedInstanceId, instance?.sessionCode]);

  // Settings handlers (nutzen session.updateSettings)
  const openSettings = useCallback(() => {
    setPendingSettings({ ...settings });
    setShowSettings(true);
  }, [settings]);

  const closeSettings = useCallback(() => {
    setPendingSettings(null);
    setShowSettings(false);
  }, []);

  const updatePendingSettings = useCallback((newSettings) => {
    setPendingSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const saveSettings = useCallback(async () => {
    if (!pendingSettings) return;
    await session.updateSettings(pendingSettings);
    setPendingSettings(null);
    setShowSettings(false);
  }, [pendingSettings, session]);

  // Legacy wrapper for backwards compatibility
  const updateQuizSettings = useCallback(async (newSettings) => {
    const mergedSettings = { ...settings, ...newSettings };
    await session.updateSettings(mergedSettings);
  }, [settings, session]);

  // Keyboard shortcuts - nutzt useKeyboardShortcuts Hook
  useKeyboardShortcuts({
    onPrevious: handlePreviousQuestion,
    onNext: handleNextQuestion,
    onFirst: handleFirstQuestion,
    onLast: handleLastQuestion,
    onToggleResults: handleShowResults,
    onRevealAnswer: handleRevealAnswer,
    onLockAnswers: handleLockAnswers
  }, quizStarted); // Nur aktiv wenn Quiz gestartet

  // Calculate response stats
  const getResponseStats = () => {
    const question = questions[currentQuestionIndex];
    if (!question || !question.options) return { counts: {}, total: 0, percentages: {} };

    const counts = {};
    question.options.forEach((_, index) => {
      counts[index] = 0;
    });

    let total = 0;
    Object.values(currentAnswers).forEach(answer => {
      const answerIndex = typeof answer === 'object' ? answer.selectedIndex : answer;
      if (answerIndex !== undefined && answerIndex !== null) {
        counts[answerIndex] = (counts[answerIndex] || 0) + 1;
        total++;
      }
    });

    const percentages = {};
    Object.keys(counts).forEach(key => {
      percentages[key] = total > 0 ? (counts[key] / total) * 100 : 0;
    });

    return { counts, total, percentages };
  };

  if (loading) {
    return <LoadingScreen message="Quiz wird geladen..." background="dark" />;
  }

  if (!instance) {
    return (
      <ErrorScreen
        type="notFound"
        message="Quiz nicht gefunden"
        backTo="/dashboard"
        backLabel="Zum Dashboard"
        background="dark"
      />
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const { counts, total, percentages } = getResponseStats();
  const participantCount = Object.keys(participants).length;
  const responseRate = participantCount > 0 ? Math.round((total / participantCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white flex flex-col theme-transition">
      <PageHeader
        title={instance.title || settings?.displayName || 'Quiz Session'}
        subtitle="Live Quiz Session"
        onBack="/dashboard"
        actions={[
          {
            label: '',
            onClick: () => setShowBugReport(true),
            variant: 'secondary',
            icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>,
            title: 'Bug Report'
          }
        ]}
        centerContent={
          <a
            href="https://www.bil-hamburg.de"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition-fast group"
            title="Made with ❤️ by BIL Hamburg"
          >
            <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
              Made with
            </span>
            <span className="text-red-500">❤️</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
              by
            </span>
            <img
              src="/LogoDruckColorOnBrightUncoatedA0.png"
              alt="BIL Hamburg"
              className="h-8 dark:hidden"
            />
            <img
              src="/LogoDruckColorOnDarkUncoatedA0.png"
              alt="BIL Hamburg"
              className="h-8 hidden dark:block"
            />
          </a>
        }
        primaryAction={{
          label: 'Quiz auswerten',
          onClick: handleEndQuiz,
          variant: 'danger'
        }}
      />

      {/* Session Actions Bar */}
      <SessionActionsBar
        sessionCode={instance.sessionCode}
        onCopyLink={copyParticipantLink}
        copySuccess={copySuccess}
        showQRCode={showQRCode}
        onToggleQRCode={() => setShowQRCode(!showQRCode)}
        showLeaderboard={showLeaderboard}
        onToggleLeaderboard={() => setShowLeaderboard(!showLeaderboard)}
        showStatistics={showStatistics}
        onToggleStatistics={() => setShowStatistics(!showStatistics)}
        showParticipants={showParticipants}
        onToggleParticipants={() => setShowParticipants(!showParticipants)}
        onOpenSettings={openSettings}
        onResetQuiz={handleResetQuiz}
        participantCount={participantCount}
        participants={participants}
      />

      {/* Floating QR Code Panel */}
      {showQRCode && (
        <QRCodeFloatingPanel
          sessionCode={instance.sessionCode || resolvedInstanceId}
          onClose={() => setShowQRCode(false)}
        />
      )}

      {/* Settings Modal */}
      <QuizSettingsModal
        isOpen={showSettings}
        onClose={closeSettings}
        onSave={saveSettings}
        settings={pendingSettings}
        updateSettings={updatePendingSettings}
        instanceSessionCode={instance?.sessionCode || resolvedInstanceId}
      />

      {/* Bug Report Modal */}
      <BugReportModal 
        isOpen={showBugReport}
        onClose={() => setShowBugReport(false)}
      />

      {/* Main Content - Lobby or Question Display */}
      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* LOBBY SCREEN - waiting for teacher to start */}
          {!quizStarted ? (
            <LobbyScreen
              instance={instance}
              participants={participants}
              settings={settings}
              questions={questions}
              onStartQuiz={handleStartQuiz}
            />
          ) : (
            <>
              {/* Question Number & Progress */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-gray-500 dark:text-gray-400 text-lg">
                  Frage {currentQuestionIndex + 1} von {questions.length}
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-64 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 transition-all duration-300"
                      style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Timer Display */}
              {(settings?.enableTimer || settings?.enableQuestionTimer) && (
                <TimerControls
                  timeRemaining={timeRemaining}
                  timerActive={timerActive}
                  timerPaused={timerPaused}
                  onStart={handleStartTimer}
                  onPause={handlePauseTimer}
                  onResume={handleResumeTimer}
                  onStop={handleStopTimer}
                  onAddTime={handleAddTime}
                />
              )}

              {/* Question Card */}
              <QuestionCard
                question={currentQuestion}
                responseStats={{ counts, total, percentages }}
                participantCount={participantCount}
                showResults={showResults}
                showCorrectAnswer={showCorrectAnswer}
              />

          {/* Question Controls */}
          <QuestionControlPanel
            // Results state
            showResults={showResults}
            showCorrectAnswer={showCorrectAnswer}
            answersLocked={answersLocked}
            onShowResults={handleShowResults}
            onLockAnswers={() => handleLockAnswers()}
            onRevealAnswer={handleRevealAnswer}
            // Navigation
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            onFirstQuestion={handleFirstQuestion}
            onPreviousQuestion={handlePreviousQuestion}
            onNextQuestion={handleNextQuestion}
            onLastQuestion={handleLastQuestion}
            onBackToLobby={handleBackToLobby}
            // Admin features
            showLeaderboard={showLeaderboard}
            showStatistics={showStatistics}
            onToggleLeaderboard={() => setShowLeaderboard(!showLeaderboard)}
            onToggleStatistics={() => setShowStatistics(!showStatistics)}
            onExportQuestions={exportQuestions}
            onExportResults={exportResults}
            hasResults={canExportResults}
          />

          {/* Leaderboard Modal */}
          <LeaderboardModal
            isOpen={showLeaderboard}
            onClose={() => setShowLeaderboard(false)}
            participants={participants}
            answers={allAnswers}
            questions={questions}
          />

          {/* Statistics Modal */}
          <StatisticsModal
            isOpen={showStatistics}
            onClose={() => setShowStatistics(false)}
            participants={participants}
            answers={allAnswers}
            questions={questions}
            currentQuestion={currentQuestionIndex}
          />

            </>
          )}
        </div>
      </main>

      {/* End Quiz Confirmation */}
      <ConfirmDialog
        isOpen={showEndQuizConfirm}
        onClose={() => setShowEndQuizConfirm(false)}
        onConfirm={confirmEndQuiz}
        title="Quiz auswerten?"
        message="Das Quiz wird beendet und alle Teilnehmer sehen die Endergebnisse. Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Quiz auswerten"
        cancelLabel="Abbrechen"
        variant="danger"
        icon="🏁"
      />

      {/* Reset Quiz Confirmation */}
      <ConfirmDialog
        isOpen={showResetQuizConfirm}
        onClose={() => setShowResetQuizConfirm(false)}
        onConfirm={confirmResetQuiz}
        title="Quiz neu starten?"
        message="Das Quiz wird komplett zurückgesetzt: Alle Punkte, Joker, Antworten und der Fortschritt werden gelöscht. Die Teilnehmer bleiben verbunden und das Quiz startet wieder von der Lobby."
        confirmLabel="Neu starten"
        cancelLabel="Abbrechen"
        variant="warning"
      />
    </div>
  );
}

export default LiveModeView;
